/** Champ birthdate unifié (segments, rules, internal jobs). */
export const BIRTHDATE_FIELD = "customer.birthdate";

export const BIRTHDATE_GRANULARITY = {
    FULL: "full",
    DAY_MONTH: "day_month",
    MONTH: "month",
    YEAR: "year",
};

const MONTH_NAMES_FR = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export const DEFAULT_BIRTHDATE_GRANULARITIES = [
    { key: "full", label: "Date complète", hint: "jj/mm/aaaa" },
    { key: "day_month", label: "Jour + mois (anniversaire)", hint: "jj/mm" },
    { key: "month", label: "Mois seul", hint: "mm" },
    { key: "year", label: "Année seule", hint: "aaaa" },
];

function pad2(n) {
    return String(n).padStart(2, "0");
}

export function isBirthdateSystemToday(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value) && value.$system === "today";
}

export function makeBirthdateSystemToday() {
    return { $system: "today" };
}

/** Déduit la granularité à partir d'une valeur wire (sans /). */
export function inferBirthdateGranularity(wire) {
    if (!wire || typeof wire !== "string") return BIRTHDATE_GRANULARITY.FULL;
    if (/^\d{4}$/.test(wire)) return BIRTHDATE_GRANULARITY.YEAR;
    if (/^--\d{2}$/.test(wire) || /^\d{2}$/.test(wire)) return BIRTHDATE_GRANULARITY.MONTH;
    if (/^\d{2}-\d{2}$/.test(wire)) return BIRTHDATE_GRANULARITY.DAY_MONTH;
    if (/^\d{4}-\d{2}-\d{2}$/.test(wire)) return BIRTHDATE_GRANULARITY.FULL;
    return BIRTHDATE_GRANULARITY.FULL;
}

/** wireValue → affichage fr-FR */
export function birthdateWireToDisplay(granularity, wire) {
    if (!wire || typeof wire !== "string") return "";
    if (wire.includes("/")) return wire;

    switch (granularity) {
        case BIRTHDATE_GRANULARITY.FULL: {
            const m = wire.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            return m ? `${m[3]}/${m[2]}/${m[1]}` : wire;
        }
        case BIRTHDATE_GRANULARITY.DAY_MONTH: {
            const m = wire.match(/^(\d{2})-(\d{2})$/);
            return m ? `${m[2]}/${m[1]}` : wire;
        }
        case BIRTHDATE_GRANULARITY.MONTH: {
            const mo = wire.replace(/^--/, "");
            const n = parseInt(mo, 10);
            if (n >= 1 && n <= 12) return MONTH_NAMES_FR[n - 1];
            return mo;
        }
        case BIRTHDATE_GRANULARITY.YEAR:
            return wire;
        default:
            return wire;
    }
}

function parseFrenchDisplay(displayValue, granularity) {
    const s = String(displayValue || "").trim();
    if (!s) return null;

    if (granularity === BIRTHDATE_GRANULARITY.FULL) {
        const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return null;
        return { day: pad2(m[1]), month: pad2(m[2]), year: m[3] };
    }
    if (granularity === BIRTHDATE_GRANULARITY.DAY_MONTH) {
        const m = s.match(/^(\d{1,2})\/(\d{1,2})$/);
        if (!m) return null;
        return { day: pad2(m[1]), month: pad2(m[2]) };
    }
    if (granularity === BIRTHDATE_GRANULARITY.MONTH) {
        const num = s.match(/^(\d{1,2})$/);
        if (num) {
            const n = parseInt(num[1], 10);
            if (n >= 1 && n <= 12) return { month: pad2(n) };
        }
        const idx = MONTH_NAMES_FR.findIndex((name) => name.toLowerCase() === s.toLowerCase());
        if (idx >= 0) return { month: pad2(idx + 1) };
        return null;
    }
    if (granularity === BIRTHDATE_GRANULARITY.YEAR) {
        const m = s.match(/^(\d{4})$/);
        return m ? { year: m[1] } : null;
    }
    return null;
}

/** displayValue (fr-FR) → wireValue API (jamais de /) */
export function serializeBirthdateWire(granularity, displayValue) {
    if (displayValue == null || displayValue === "") return "";
    const parts = parseFrenchDisplay(displayValue, granularity);
    if (!parts) return "";

    switch (granularity) {
        case BIRTHDATE_GRANULARITY.FULL:
            return `${parts.year}-${parts.month}-${parts.day}`;
        case BIRTHDATE_GRANULARITY.DAY_MONTH:
            return `${parts.month}-${parts.day}`;
        case BIRTHDATE_GRANULARITY.MONTH:
            return parts.month;
        case BIRTHDATE_GRANULARITY.YEAR:
            return parts.year;
        default:
            return "";
    }
}

/** Garantit qu'aucune valeur wire ne contient de slash. */
export function normalizeBirthdateWireValue(wireOrDisplay, granularity) {
    if (wireOrDisplay == null || wireOrDisplay === "") return "";
    if (typeof wireOrDisplay === "object") return wireOrDisplay;
    const s = String(wireOrDisplay);
    if (s.includes("/")) return serializeBirthdateWire(granularity, s);
    return s;
}

export function normalizeBirthdateConditionValue(field, value, granularityHint) {
    if (field !== BIRTHDATE_FIELD) return value ?? "";
    if (isBirthdateSystemToday(value)) return value;
    if (Array.isArray(value)) {
        const gran = granularityHint || inferBirthdateGranularity(String(value[0] || ""));
        return value.map((v) => normalizeBirthdateWireValue(v, gran));
    }
    const gran = granularityHint || inferBirthdateGranularity(String(value || ""));
    return normalizeBirthdateWireValue(value, gran);
}

export function resolveBirthdateGranularities(fieldMeta) {
    const fromApi = fieldMeta?.granularities;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
        return fromApi.map((g) => ({
            key: g.key || g.id || g.value,
            label: g.label || g.name || g.key,
            hint: g.hint || g.displayFormat || "",
        })).filter((g) => g.key);
    }
    return DEFAULT_BIRTHDATE_GRANULARITIES;
}

export { MONTH_NAMES_FR };
